/**
 * Pure wire builders for the My Testing Devices command dialogs — each mirrors the exact call scio3's
 * dialog makes (screens/components/dialogs/*), vendored by copy. React-free so the payloads are
 * asserted directly in tests.
 */
import type { TestingDeviceDetail } from './types';

// ---------------------------------------------------------------------------
// Errors — Apollo 4 (CombinedGraphQLErrors.errors) and older (graphQLErrors) shapes
// ---------------------------------------------------------------------------

/** The server's own message (e.g. staff-api's 400 "setting not allowed: <key>"), else the fallback. */
export function errorText(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const e = err as {
      errors?: Array<{ message?: string }>;
      graphQLErrors?: Array<{ message?: string }>;
      message?: string;
    };
    const msg = e.errors?.[0]?.message ?? e.graphQLErrors?.[0]?.message ?? e.message;
    if (msg) return msg;
  }
  return fallback;
}

// ---------------------------------------------------------------------------
// Time zone (mdmDetails GET_TIMEZONE → mdmCommand SET_TIMEZONE)
// ---------------------------------------------------------------------------

/** The device's current zone from a GET_TIMEZONE reply (possibly double-encoded by staff-api). */
export function parseTimezoneReply(raw: unknown): string | null {
  let v: unknown = raw;
  try {
    for (let i = 0; i < 3 && typeof v === 'string'; i += 1) {
      const s: string = v;
      if (!s) return null;
      if (!/^[[{"]/.test(s.trim())) return s; // a bare zone name
      v = JSON.parse(s);
    }
  } catch {
    return typeof v === 'string' ? v : null;
  }
  if (v && typeof v === 'object') {
    const o = v as { timezone_string?: unknown; timezone?: unknown; data?: unknown };
    const inner = (o.data && typeof o.data === 'object' ? o.data : o) as {
      timezone_string?: unknown;
      timezone?: unknown;
    };
    const tz = inner.timezone_string ?? inner.timezone;
    return typeof tz === 'string' && tz ? tz : null;
  }
  return null;
}

/** IANA zones the browser knows (Intl), with the device's current one guaranteed present. */
export function timezoneOptions(current?: string | null): string[] {
  let zones: string[] = [];
  try {
    const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
    zones = intl.supportedValuesOf?.('timeZone') ?? [];
  } catch {
    zones = [];
  }
  if (!zones.includes('UTC')) zones = ['UTC', ...zones];
  if (current && !zones.includes(current)) zones = [current, ...zones];
  return zones;
}

// ---------------------------------------------------------------------------
// Wi-Fi (mdmDetails GET_WIFI → mdmCommand ADD_WIFI_AP / REMOVE_WIFI_AP)
// ---------------------------------------------------------------------------

export interface WifiAccessPoint {
  wifi_ssid: string;
  wifi_password?: string;
  wifi_security_type?: string;
  wifi_phase2_auth?: string;
  hidden?: boolean;
}

/** scio1's two security types. */
export const WIFI_SECURITY_TYPES = [
  { value: 'WEP', label: 'WEP' },
  { value: 'WPA2', label: 'WPA/WPA2' },
] as const;

export function parseWifiReply(raw: unknown): WifiAccessPoint[] {
  let v: unknown = raw;
  try {
    for (let i = 0; i < 3 && typeof v === 'string'; i += 1) {
      if (!v) return [];
      v = JSON.parse(v);
    }
  } catch {
    return [];
  }
  const pick = (x: unknown): unknown[] | null => (Array.isArray(x) ? x : null);
  const o = (v ?? {}) as { data?: { wifi_access_points?: unknown }; wifi_access_points?: unknown };
  const list = pick(v) ?? pick(o.data?.wifi_access_points) ?? pick(o.wifi_access_points) ?? [];
  return list.filter(
    (ap): ap is WifiAccessPoint =>
      !!ap && typeof ap === 'object' && typeof (ap as WifiAccessPoint).wifi_ssid === 'string',
  );
}

/** ADD_WIFI_AP value — one access point, scio1 field names, phase-2 auth NONE. */
export function addWifiValue(input: {
  ssid: string;
  password: string;
  securityType: string;
  hidden: boolean;
}): string {
  return JSON.stringify([
    {
      wifi_ssid: input.ssid,
      wifi_password: input.password,
      wifi_security_type: input.securityType,
      wifi_phase2_auth: 'NONE',
      hidden: input.hidden,
    },
  ]);
}

/** REMOVE_WIFI_AP value — the SSID list. */
export function removeWifiValue(ssid: string): string {
  return JSON.stringify([ssid]);
}

/** scio1 validation order; '' = valid. */
export function validateWifi(input: { ssid: string; password: string; securityType: string }): string {
  if (!input.ssid.trim()) return 'Wi-Fi SSID is required';
  if (!input.securityType) return 'Wi-Fi Security Type is required';
  if (!input.password) return 'Wi-Fi Password is required';
  if (input.password.length < 8) return 'Wi-Fi Password must be at least 8 characters';
  return '';
}

// ---------------------------------------------------------------------------
// Install OptiSigns version (CDN list → addCommandExecution MDM_INSTALL_OPTISIGNS_VERSION)
// ---------------------------------------------------------------------------

export const ANDROID_VERSIONS_URL =
  'https://cdn.optisigns.com/android-optisigns-versions/androidOptiSignsVersions.json';
export const ELECTRON_VERSIONS_URL =
  'https://cdn.optisigns.com/electron-optisigns-versions/proPlayerOptiSignsVersions.json';

export type VersionCategory = 'android' | 'promax' | 'pro-gen2';

export interface VersionEntry {
  version: string;
  url?: string;
  [key: string]: unknown;
}

/**
 * scio3 `getDeviceCategory`: OptiSigns Electron hardware (ProPlayer) → electron list, the x64 Ubuntu
 * ProPlayer being 'pro-gen2'; everything else → android. scio3 keys off `device.device === 'electron'`,
 * which the testing detail lacks — manufacturer + platform/hardware family stand in for it.
 */
export function versionCategory(d: TestingDeviceDetail | null | undefined): VersionCategory {
  const hw = (d?.classification?.hardwareFamily ?? '').toLowerCase();
  const platform = (d?.platform ?? d?.classification?.platform ?? '').toLowerCase();
  const isOpsElectron =
    hw.startsWith('optisigns-pro') ||
    ((d?.manufacturer ?? '').toLowerCase() === 'optisigns' && platform.includes('electron'));
  if (!isOpsElectron) return 'android';
  return d?.model === 'optisigns_x64' ? 'pro-gen2' : 'promax';
}

export function versionsUrl(category: VersionCategory): string {
  return category === 'android' ? ANDROID_VERSIONS_URL : ELECTRON_VERSIONS_URL;
}

/** scio3 `getVersionKey`: model pins the build, then category, then `typeBuild` ('android11', …). */
export function versionKey(category: VersionCategory, d: TestingDeviceDetail | null | undefined): string {
  if (d?.model === 'optisigns_pro') return 'pro';
  if (d?.model === 'optisigns_pro_max') return 'promax';
  if (category === 'promax') return 'promax';
  if (category === 'pro-gen2') return 'pro';
  return d?.typeBuild || 'android';
}

/** The version list for this device out of the CDN JSON (key → 'other' → []). */
export function versionEntries(data: unknown, key: string): VersionEntry[] {
  if (!data || typeof data !== 'object') return [];
  const o = data as Record<string, unknown>;
  const list = o[key] ?? o.other;
  return Array.isArray(list)
    ? list.filter((e): e is VersionEntry => !!e && typeof (e as VersionEntry).version === 'string')
    : [];
}

export function installVersionValue(entry: VersionEntry, isDisableAutoUpdate: boolean): string {
  return JSON.stringify({ ...entry, isDisableAutoUpdate });
}

// ---------------------------------------------------------------------------
// Install APK (addCommandExecution MDM_INSTALL_APK, value = the URL)
// ---------------------------------------------------------------------------

/** scio1 installAPKSendModal URL check. */
const APK_URL_REGEX =
  /(((https?)|ftp):\/\/([-\w]+\.)+\w{2,3}(\/[%\-\w]+(\.\w{2,})?)*(([\w\-.?/+@&#;`~=%!]*)(\.\w{2,})?)*\/?)/i;

export function validateApkUrl(url: string): string {
  const v = url.trim();
  if (!v) return 'URL is required';
  if (!APK_URL_REGEX.test(v)) return 'URL is invalid';
  return '';
}

// ---------------------------------------------------------------------------
// OTA (updateDevice feature.ota* ; "check update" = executeRemoteCommand forceOTA)
// ---------------------------------------------------------------------------

const OTA_DAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const;
export const DEFAULT_OTA_RRULE = 'RRULE:FREQ=WEEKLY;BYDAY=SU;BYHOUR=2;BYMINUTE=0;BYSECOND=0';

export interface OtaValues {
  channel: 'stable' | 'beta';
  days: number[];
  hour: number;
  minute: number;
  reset: boolean;
}

export function parseOtaRRule(rrule: unknown): { days: number[]; hour: number; minute: number } {
  const str = typeof rrule === 'string' && rrule ? rrule : DEFAULT_OTA_RRULE;
  const byDay = /BYDAY=([A-Z,]+)/.exec(str)?.[1];
  const days = byDay
    ? byDay
        .split(',')
        .map((d) => OTA_DAYS.indexOf(d as (typeof OTA_DAYS)[number]))
        .filter((d) => d >= 0)
    : [0];
  const hour = Number(/BYHOUR=(\d+)/.exec(str)?.[1] ?? 2);
  const minute = Number(/BYMINUTE=(\d+)/.exec(str)?.[1] ?? 0);
  return { days: days.length ? days : [0], hour, minute };
}

export function buildOtaRRule(days: number[], hour: number, minute: number): string {
  const valid = days.length ? [...days].sort((a, b) => a - b) : [0];
  return `RRULE:FREQ=WEEKLY;BYDAY=${valid.map((d) => OTA_DAYS[d]).join(',')};BYHOUR=${hour};BYMINUTE=${minute};BYSECOND=0`;
}

export function otaBaseline(feature: Record<string, unknown> | null | undefined): OtaValues {
  const f = feature ?? {};
  return {
    channel: f.otaChannel === 'beta' ? 'beta' : 'stable',
    ...parseOtaRRule(f.otaScheduleRRule),
    reset: Boolean(f.otaReset),
  };
}

/** The OTA dialog's save — always all three keys, like scio3. */
export function otaPatch(v: OtaValues): { feature: Record<string, unknown> } {
  return {
    feature: {
      otaChannel: v.channel,
      otaScheduleRRule: buildOtaRRule(v.days, v.hour, v.minute),
      otaReset: v.reset,
    },
  };
}

// ---------------------------------------------------------------------------
// HDMI CEC (addCommandExecution MDM_HDMI_CEC)
// ---------------------------------------------------------------------------

export function hdmiCecValue(action: 'on' | 'off'): string {
  return action === 'on' ? 'cecHDMIOn' : 'cecHDMIOff';
}

// ---------------------------------------------------------------------------
// RS232 (addCommandExecution MDM_RS232)
// ---------------------------------------------------------------------------

export interface Rs232Port {
  _id: string;
  name?: string;
  path?: string;
  baudRate?: number;
  dataBits?: number;
  stopBits?: number;
  parity?: string;
  rtscts?: boolean;
  xon?: boolean;
  xoff?: boolean;
  eol?: string;
  encoding?: string;
}

export interface Rs232Command {
  _id: string;
  name?: string;
  value?: string;
  eol?: string;
  encoding?: string;
}

export const RS232_ENCODINGS = ['ascii', 'hex'] as const;

/** EOL: stored value → character sent (TestNewCommands sends the character, like scio3). */
export const RS232_EOL_OPTIONS = [
  { label: 'None', value: 'none', char: '' },
  { label: 'CR', value: 'CR', char: '\r' },
  { label: 'LF', value: 'LF', char: '\n' },
  { label: 'CR+LF', value: 'CRLF', char: '\r\n' },
] as const;

export function parseRs232Config(raw: unknown): { ports: Rs232Port[]; commands: Rs232Command[] } {
  let v: unknown = raw;
  try {
    if (typeof v === 'string') v = v ? JSON.parse(v) : null;
  } catch {
    v = null;
  }
  const o = (v ?? {}) as { ports?: unknown; commands?: unknown };
  const ok = <T,>(x: unknown): T[] =>
    Array.isArray(x) ? x.filter((e) => !!e && typeof (e as { _id?: unknown })._id === 'string') : [];
  return { ports: ok<Rs232Port>(o.ports), commands: ok<Rs232Command>(o.commands) };
}

/** scio3 `cleanExternalCom` — only the connection fields, never account/team/audit fields. */
function cleanPort(p: Rs232Port): Rs232Port {
  const { _id, name, path, baudRate, dataBits, stopBits, parity, rtscts, xon, xoff, eol, encoding } = p;
  return { _id, name, path, baudRate, dataBits, stopBits, parity, rtscts, xon, xoff, eol, encoding };
}

export function rs232Value(
  port: Rs232Port,
  send:
    | { type: 'Commands'; command: Rs232Command }
    | { type: 'TestNewCommands'; value: string; encoding: string; eol: string },
): string {
  const data =
    send.type === 'Commands'
      ? [
          {
            value: send.command.value ?? '',
            encoding: send.command.encoding ?? 'ascii',
            eol: send.command.eol ?? 'none',
          },
        ]
      : [
          {
            value: send.value.trim(),
            encoding: send.encoding,
            eol: RS232_EOL_OPTIONS.find((o) => o.value === send.eol)?.char ?? '',
          },
        ];
  return JSON.stringify({ data, type: send.type, externalCom: cleanPort(port) });
}

/** The device's reply to a needs-response RS232 send (`{ data: { value } }`), if any. */
export function rs232Reply(reply: unknown): string | null {
  const v = (reply as { data?: { value?: unknown } } | null)?.data?.value;
  return typeof v === 'string' && v ? v : null;
}

// ---------------------------------------------------------------------------
// Execute Remote Commands
// ---------------------------------------------------------------------------

/** scio1/scio3 presets. */
export const EXECUTE_PRESETS = [
  { value: 'refreshApp', label: 'Refresh & Relaunch' },
  { value: 'rebootDevice', label: 'Reboot Device' },
] as const;
